import { supabase } from './supabase';
import { logger } from './logger';
import { redditPixel } from './reddit-pixel';

class Analytics {
  private static instance: Analytics;

  private constructor() {}

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public track(event: string, properties: Record<string, any> = {}) {
    if (import.meta.env.PROD) {
      console.log('[Analytics]', event, properties);
    }

    this.trackRedditConversion(event, properties);
  }

  private trackRedditConversion(event: string, properties: Record<string, any>) {
    switch (event) {
      case AnalyticsEvents.SUBSCRIPTION_STARTED:
      case AnalyticsEvents.PAYMENT_SUCCEEDED:
        redditPixel.trackPurchase(
          properties.value || properties.amount || 0,
          properties.transactionId || properties.subscriptionId
        );
        break;
      case 'user_signed_up':
        redditPixel.trackSignUp();
        break;
      case 'trial_started':
        redditPixel.trackLead();
        break;
      default:
        break;
    }
  }
}

export const analytics = Analytics.getInstance();

export const AnalyticsEvents = {
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  PAYMENT_SUCCEEDED: 'payment_succeeded',
  PAYMENT_FAILED: 'payment_failed',
  USER_SIGNED_UP: 'user_signed_up',
  TRIAL_STARTED: 'trial_started'
} as const;

// Analytics data retrieval
export async function getAnalytics(startDate: Date, endDate: Date) {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) return [];

    const { data, error } = await supabase
      .from('analytics_events')
      .select('*')
      .eq('user_id', user.id)
      .gte('timestamp', startDate.toISOString())
      .lte('timestamp', endDate.toISOString())
      .order('timestamp', { ascending: true });

    if (error) {
      logger.error('Error fetching analytics:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('Error fetching analytics:', error);
    return [];
  }
}

// Get analytics summary
export async function getAnalyticsSummary() {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) return { events: [], stats: null };

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Get event counts - fixed to use a proper query chain
    const { data: eventsData, error: eventsError } = await supabase
      .from('analytics_events')
      .select('event')
      .eq('user_id', user.id)
      .gte('timestamp', thirtyDaysAgo.toISOString());
    
    if (eventsError) {
      logger.error('Error fetching analytics events:', eventsError);
      return { events: [], stats: null };
    }
    
    // Process events to get counts
    const events = eventsData ? eventsData.reduce((acc: Record<string, number>, item: any) => {
      acc[item.event] = (acc[item.event] || 0) + 1;
      return acc;
    }, {}) : {};

    // Get user stats
    const { data: stats, error: statsError } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (statsError && statsError.code !== 'PGRST116') {
      logger.error('Error fetching user stats:', statsError);
    }

    return {
      events: Object.entries(events).map(([event, count]) => ({ event, count })),
      stats: stats || null
    };
  } catch (error) {
    logger.error('Error fetching analytics summary:', error);
    return { events: [], stats: null };
  }
}